import { apiRequest } from './api';
import { state } from './state';
import { navigateTo } from './router';
import { t } from './i18n';
import { openGameDetailModal } from './ui/player-detail';

// ============================================================
// «С прошлого визита»: при заходе показываем, что изменилось
// (новые игры, свои результаты, MMR, уведомления, подписчики).
// Метка прошлого визита хранится в localStorage — без новых таблиц.
// Последний показанный дайджест сохраняется уведомлением, чтобы его
// можно было пересмотреть по клику (непрочитанным он не становится).
// ============================================================

const LAST_SEEN_KEY = 'recap.last_seen';
/** Не показываем чаще, чем раз в 6 часов. */
const MIN_GAP_MS = 6 * 3_600_000;

interface RecapGame {
  id: number;
  played_at: string;
  map_name: string | null;
  player_count: number;
  winners: string[];
  won?: boolean;
  elo_delta?: number | null;
}

export interface RecapResponse {
  since: string;
  until: string;
  clamped?: boolean;
  public: { games: number; players: number; recent_games: RecapGame[] };
  me: {
    player: { id: number; name: string; elo: number; rank: number | null };
    games: number;
    wins: number;
    elo_delta: number;
    recent_games: RecapGame[];
    games_to_react?: Array<{ id: number; played_at: string; map_name: string | null }>;
  } | null;
  notifications: number;
  new_followers: number | null;
}

function closeModal(): void {
  document.querySelector('.recap-overlay')?.remove();
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function item(icon: string, label: string, value: string, tone?: 'plus' | 'minus'): HTMLLIElement {
  const li = document.createElement('li');
  li.className = 'recap-item';

  const iconEl = document.createElement('span');
  iconEl.className = 'recap-item-icon';
  iconEl.textContent = icon;

  const labelEl = document.createElement('span');
  labelEl.textContent = label;

  const valueEl = document.createElement('span');
  valueEl.className = 'recap-item-value' + (tone ? ` is-${tone}` : '');
  valueEl.textContent = value;

  li.append(iconEl, labelEl, valueEl);
  return li;
}

function buildModal(data: RecapResponse): HTMLElement {
  const overlay = document.createElement('div');
  overlay.className = 'recap-overlay';

  const card = document.createElement('div');
  card.className = 'recap-card';

  const head = document.createElement('div');
  head.className = 'recap-head';

  const titles = document.createElement('div');
  const title = document.createElement('h2');
  title.className = 'recap-title';
  title.textContent = t('recap.title');
  const subtitle = document.createElement('p');
  subtitle.className = 'recap-subtitle';
  subtitle.textContent = t('recap.subtitle');
  titles.append(title, subtitle);

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'recap-close';
  close.textContent = '✕';
  close.setAttribute('aria-label', t('recap.dismiss'));
  close.addEventListener('click', closeModal);

  head.append(titles, close);

  const list = document.createElement('ul');
  list.className = 'recap-list';

  if (data.public.games > 0) {
    list.appendChild(item('⚔️', t('recap.games'), String(data.public.games)));
  }
  if (data.public.players > 0) {
    list.appendChild(item('👥', t('recap.players'), String(data.public.players)));
  }

  if (data.me) {
    list.appendChild(
      item('🎮', t('recap.your_games'), `${data.me.wins}/${data.me.games}`),
    );

    const delta = data.me.elo_delta;
    list.appendChild(
      item(
        '📈',
        t('recap.mmr'),
        `${delta >= 0 ? '+' : ''}${delta}`,
        delta > 0 ? 'plus' : delta < 0 ? 'minus' : undefined,
      ),
    );

    if (data.me.player.rank != null) {
      list.appendChild(item('🏆', t('recap.rank'), `#${data.me.player.rank}`));
    }
  }

  if (data.notifications > 0) {
    list.appendChild(item('🔔', t('recap.notifications'), String(data.notifications)));
  }
  if (data.new_followers != null && data.new_followers > 0) {
    list.appendChild(item('⭐', t('recap.followers'), `+${data.new_followers}`));
  }

  card.append(head, list);

  // Последняя игра — короткой строкой.
  const lastGame = data.me?.recent_games?.[0] ?? data.public.recent_games?.[0] ?? null;
  if (lastGame) {
    const line = document.createElement('div');
    line.className = 'recap-last-game';

    const label = document.createElement('div');
    label.textContent = t('recap.last_game');

    const strong = document.createElement('strong');
    strong.textContent = lastGame.map_name ?? '—';

    const meta = document.createElement('div');
    const winners = (lastGame.winners ?? []).join(' + ');
    meta.textContent = `${formatDate(lastGame.played_at)}${winners ? ` · ${winners}` : ''}`;

    line.append(label, strong, meta);
    card.appendChild(line);
  }

  // Игры, которые пользователь ещё не оценил (реакции/MVP) — коротким списком.
  const toReact = data.me?.games_to_react ?? [];
  if (toReact.length > 0) {
    const block = document.createElement('div');
    block.className = 'recap-react';

    const blockTitle = document.createElement('div');
    blockTitle.className = 'recap-react-title';
    blockTitle.textContent = t('recap.react_title');

    const blockHint = document.createElement('div');
    blockHint.className = 'recap-react-hint';
    blockHint.textContent = t('recap.react_hint');

    const rows = document.createElement('div');
    rows.className = 'recap-react-list';
    for (const game of toReact) {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'recap-react-item';

      const map = document.createElement('span');
      map.className = 'recap-react-map';
      map.textContent = game.map_name ?? '—';

      const meta = document.createElement('span');
      meta.className = 'recap-react-meta';
      meta.textContent = formatDate(game.played_at);

      row.append(map, meta);
      row.addEventListener('click', () => {
        closeModal();
        void openGameDetailModal(game.id);
      });
      rows.appendChild(row);
    }

    block.append(blockTitle, blockHint, rows);
    card.appendChild(block);
  }

  if (list.childElementCount === 0) {
    const empty = document.createElement('p');
    empty.className = 'hint';
    empty.textContent = t('recap.empty');
    card.appendChild(empty);
  }

  const actions = document.createElement('div');
  actions.className = 'recap-actions';

  const openGames = document.createElement('button');
  openGames.type = 'button';
  openGames.className = 'btn-primary';
  openGames.textContent = t('recap.open_games');
  openGames.addEventListener('click', () => {
    closeModal();
    navigateTo('/games');
  });

  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.className = 'btn-secondary';
  dismiss.textContent = t('recap.dismiss');
  dismiss.addEventListener('click', closeModal);

  actions.append(openGames, dismiss);
  card.appendChild(actions);

  overlay.appendChild(card);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeModal();
  });
  document.addEventListener('keydown', function onKey(e) {
    if (e.key !== 'Escape') return;
    document.removeEventListener('keydown', onKey);
    closeModal();
  });

  return overlay;
}

/** Показать дайджест (используется и при заходе, и при клике по уведомлению). */
export function showRecapModal(data: RecapResponse): void {
  closeModal();
  document.body.appendChild(buildModal(data));
}

/**
 * Принудительный показ дайджеста (тестовая кнопка админа): без паузы 6 часов
 * и без сохранения уведомления. Опорная точка — прошлый визит, иначе 7 дней.
 */
export async function forceRecap(): Promise<void> {
  const stored = localStorage.getItem(LAST_SEEN_KEY);
  const parsed = stored ? Date.parse(stored) : NaN;
  const now = Date.now();
  const since =
    Number.isFinite(parsed) && parsed < now ? parsed : now - 7 * 24 * 3_600_000;

  const data = await apiRequest<RecapResponse>(
    `/api/recap?since=${encodeURIComponent(new Date(since).toISOString())}`,
    { token: state.token },
  );
  showRecapModal(data);
}

/** Запомнить показанный дайджест уведомлением — чтобы пересмотреть по клику. */
async function rememberRecap(data: RecapResponse): Promise<void> {
  if (!state.token) return;
  try {
    await apiRequest('/api/recap/remember', {
      method: 'POST',
      token: state.token,
      body: { recap: data },
    });
  } catch {
    /* необязательный бонус */
  }
}

/** Вызывается при старте приложения. */
export async function initRecap(): Promise<void> {
  if (window.location.pathname !== '/') return;
  if (new URLSearchParams(window.location.search).has('player')) return;

  const stored = localStorage.getItem(LAST_SEEN_KEY);
  const now = Date.now();
  localStorage.setItem(LAST_SEEN_KEY, new Date(now).toISOString());

  if (!stored) return;
  const since = Date.parse(stored);
  if (!Number.isFinite(since) || now - since < MIN_GAP_MS) return;

  try {
    const data = await apiRequest<RecapResponse>(
      `/api/recap?since=${encodeURIComponent(new Date(since).toISOString())}`,
      { token: state.token },
    );

    // Нечего показывать — не мешаем.
    if (
      data.public.games === 0 &&
      !data.me?.games &&
      data.notifications === 0 &&
      !data.new_followers
    ) {
      return;
    }

    // Пользователь мог уже уйти со страницы, пока грузились данные.
    if (window.location.pathname !== '/') return;

    showRecapModal(data);
    void rememberRecap(data);
  } catch {
    /* recap — необязательный бонус, молча пропускаем */
  }
}
